/**
 * useFuturesOrderbookSubscription Hook
 *
 * Subscribes to Binance Futures orderbook for a specific symbol using CCXT Pro WebSocket.
 * Provides real-time orderbook updates for a single symbol.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Exchange as CcxtExchange } from 'ccxt';
import type { OrderbookResponse } from '../types/orderbook';

// Dynamic CCXT import (browser-compatible)
type CcxtModule = typeof import('ccxt');
type CcxtModuleWithPro = CcxtModule & { pro?: Record<string, unknown> };
let ccxtModulePromise: Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> | null = null;

async function loadCcxtModule(): Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> {
  console.log('[useFuturesOrderbookSubscription] loadCcxtModule called');
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      console.log('[useFuturesOrderbookSubscription] CCXT import successful!');
      const resolved = (raw.default ?? raw) as CcxtModuleWithPro;
      const hasProNamespace = resolved.pro && typeof resolved.pro === 'object';
      console.log('[useFuturesOrderbookSubscription] CCXT Pro namespace check:', { hasProNamespace });
      return {
        module: resolved,
        usingPro: Boolean(hasProNamespace),
      };
    }).catch((error) => {
      console.error('[useFuturesOrderbookSubscription] CCXT import FAILED:', error);
      throw error;
    });
  }
  return ccxtModulePromise;
}

export interface FuturesOrderbookState {
  orderbook: OrderbookResponse | null;
  loading: boolean;
  error: string | null;
  lastUpdate: number;
}

/**
 * Convert CCXT orderbook format to our OrderbookResponse format
 */
function convertCcxtOrderbook(ccxtOrderbook: any, symbol: string): OrderbookResponse {
  return {
    exchange: 'binance_futures',
    symbol,
    bids: ccxtOrderbook.bids.map((level: [number, number]) => ({
      price: level[0],
      volume: level[1],
    })),
    asks: ccxtOrderbook.asks.map((level: [number, number]) => ({
      price: level[0],
      volume: level[1],
    })),
    timestamp: ccxtOrderbook.timestamp || Date.now(),
  };
}

/**
 * Subscribe to Binance Futures orderbook for a specific symbol using CCXT WebSocket
 *
 * @param symbol - The trading pair symbol (e.g., "BTCUSDT")
 * @param enabled - Enable the subscription (default: true)
 */
export function useFuturesOrderbookSubscription(
  symbol: string,
  enabled: boolean = true
): FuturesOrderbookState {
  const [state, setState] = useState<FuturesOrderbookState>({
    orderbook: null,
    loading: false,
    error: null,
    lastUpdate: 0,
  });

  // Exchange client ref (stable across renders)
  const binanceFuturesRef = useRef<CcxtExchange | null>(null);

  // CCXT module loaded state
  const [ccxtLoaded, setCcxtLoaded] = useState(false);

  // Active WebSocket connection flag
  const connectionRef = useRef(false);

  /**
   * Subscribe to Binance Futures orderbook using WebSocket
   */
  const subscribeFutures = useCallback(
    async (ccxtSymbol: string) => {
      if (!binanceFuturesRef.current || !enabled) return;

      console.info(`[useFuturesOrderbookSubscription] Starting Binance Futures orderbook WebSocket: ${ccxtSymbol}`);

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        while (enabled && connectionRef.current && binanceFuturesRef.current) {
          const orderbook = await binanceFuturesRef.current.watchOrderBook(ccxtSymbol);

          // Convert to our format
          const converted = convertCcxtOrderbook(orderbook, symbol);

          setState({
            orderbook: converted,
            loading: false,
            error: null,
            lastUpdate: Date.now(),
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useFuturesOrderbookSubscription] Binance Futures WebSocket error:', errorMessage);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    },
    [enabled, symbol]
  );

  /**
   * Load CCXT module and initialize Binance Futures client on mount
   */
  useEffect(() => {
    if (!enabled) return;

    // Skip if already initialized (prevent React Strict Mode double-initialization)
    if (ccxtLoaded && binanceFuturesRef.current) {
      console.log('[useFuturesOrderbookSubscription] CCXT client already initialized, skipping');
      return;
    }

    let mounted = true;

    // Load CCXT dynamically
    loadCcxtModule().then(({ module: ccxtModule, usingPro }) => {
      if (!mounted) return;

      if (!usingPro) {
        console.warn('[useFuturesOrderbookSubscription] CCXT Pro namespace not available - WebSocket subscriptions will not work.');
        return;
      }

      console.info('[useFuturesOrderbookSubscription] Initializing Binance Futures client (CCXT Pro WebSocket)...');

      // Type assertion for pro namespace access
      type ExchangeConstructor = new (params?: Record<string, unknown>) => CcxtExchange;
      const proBinance = (ccxtModule.pro as Record<string, ExchangeConstructor>)['binance'];

      // Initialize CCXT Pro client for Binance Futures WebSocket
      binanceFuturesRef.current = new proBinance({
        enableRateLimit: true,
        options: { defaultType: 'future' }, // Important: Set to futures market
      });

      console.info('[useFuturesOrderbookSubscription] Binance Futures client initialized');
      setCcxtLoaded(true);
    }).catch((error) => {
      console.error('[useFuturesOrderbookSubscription] Failed to load CCXT module:', error);
    });

    // Cleanup on unmount
    return () => {
      mounted = false;
      console.info('[useFuturesOrderbookSubscription] Cleanup function called');
      connectionRef.current = false;
    };
  }, [enabled, ccxtLoaded]);

  /**
   * Start WebSocket subscription when symbol changes
   */
  useEffect(() => {
    if (!enabled || !ccxtLoaded || !symbol) return;

    // Convert symbol to CCXT format
    // Input: "BTCUSDT" → CCXT format: "BTC/USDT"
    const base = symbol.replace(/USDT$/, '').toUpperCase();
    const ccxtSymbol = `${base}/USDT`;

    console.info(`[useFuturesOrderbookSubscription] Starting WebSocket subscription for: ${symbol}`, {
      ccxtSymbol,
    });

    // Start WebSocket subscription
    if (!connectionRef.current) {
      connectionRef.current = true;
      subscribeFutures(ccxtSymbol);
    }

    // Cleanup function to stop WebSocket connection
    return () => {
      connectionRef.current = false;
    };
  }, [enabled, ccxtLoaded, symbol, subscribeFutures]);

  return state;
}
